<template>
  <!-- Floating Button -->
  <button
    @click="store.toggle()"
    class="a11y-fab"
    :class="{ 'a11y-fab--active': store.hasActiveSettings }"
    :aria-expanded="store.isOpen"
    aria-label="Barrierefreiheit"
    title="Barrierefreiheit"
  >
    <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z"/>
    </svg>
  </button>

  <!-- Panel -->
  <Transition name="a11y-slide">
    <div v-if="store.isOpen" class="a11y-panel" @click.self="store.close()">
      <div class="a11y-panel__content">
        <!-- Header -->
        <div class="a11y-panel__header">
          <h2 class="a11y-panel__title">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z"/>
            </svg>
            Barrierefreiheit
          </h2>
          <button @click="store.close()" class="a11y-panel__close" aria-label="Schließen">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Font Size -->
        <div class="a11y-section">
          <div class="a11y-section__label">Schriftgröße</div>
          <div class="a11y-size-control">
            <button
              @click="store.decreaseFontSize()"
              :disabled="store.fontSize <= 100"
              class="a11y-size-btn"
              aria-label="Schrift verkleinern"
            >
              <span class="text-lg">A</span>
              <span class="a11y-size-btn__icon">−</span>
            </button>
            <span class="a11y-size-value">{{ store.fontSize }}%</span>
            <button
              @click="store.increaseFontSize()"
              :disabled="store.fontSize >= 150"
              class="a11y-size-btn"
              aria-label="Schrift vergrößern"
            >
              <span class="text-xl">A</span>
              <span class="a11y-size-btn__icon">+</span>
            </button>
          </div>
        </div>

        <!-- Toggle Options -->
        <div class="a11y-toggles">
          <button
            @click="store.toggleSetting('highContrast')"
            class="a11y-toggle"
            :class="{ 'a11y-toggle--active': store.highContrast }"
          >
            <svg class="a11y-toggle__icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18V4c4.41 0 8 3.59 8 8s-3.59 8-8 8z"/>
            </svg>
            <span>Hoher Kontrast</span>
          </button>

          <button
            @click="store.toggleSetting('lineSpacing')"
            class="a11y-toggle"
            :class="{ 'a11y-toggle--active': store.lineSpacing }"
          >
            <svg class="a11y-toggle__icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 7h2.5L5 3.5 1.5 7H4v10H1.5L5 20.5 8.5 17H6V7zm4-2v2h12V5H10zm0 14h12v-2H10v2zm0-6h12v-2H10v2z"/>
            </svg>
            <span>Zeilenabstand</span>
          </button>

          <button
            @click="store.toggleSetting('letterSpacing')"
            class="a11y-toggle"
            :class="{ 'a11y-toggle--active': store.letterSpacing }"
          >
            <svg class="a11y-toggle__icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M22 7h-2v1h-5V7h-2v1H8V7H6v1H1v2h5v1H1v2h5v1H1v2h5v1H1v2h20v-2h-5v-1h5v-2h-5v-1h5v-2h-5v-1h5V7z"/>
            </svg>
            <span>Buchstabenabstand</span>
          </button>

          <button
            @click="store.toggleSetting('highlightLinks')"
            class="a11y-toggle"
            :class="{ 'a11y-toggle--active': store.highlightLinks }"
          >
            <svg class="a11y-toggle__icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
            </svg>
            <span>Links hervorheben</span>
          </button>

          <button
            @click="store.toggleSetting('stopAnimations')"
            class="a11y-toggle"
            :class="{ 'a11y-toggle--active': store.stopAnimations }"
          >
            <svg class="a11y-toggle__icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
            </svg>
            <span>Animationen stoppen</span>
          </button>

          <button
            @click="store.toggleSetting('readingRuler')"
            class="a11y-toggle"
            :class="{ 'a11y-toggle--active': store.readingRuler }"
          >
            <svg class="a11y-toggle__icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h18v8zM9 10h6v4H9z"/>
            </svg>
            <span>Leselineal</span>
          </button>

          <button
            @click="store.toggleSetting('largeCursor')"
            class="a11y-toggle"
            :class="{ 'a11y-toggle--active': store.largeCursor }"
          >
            <svg class="a11y-toggle__icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13.64 21.97C13.14 22.21 12.54 22 12.31 21.5L10.13 16.76L7.62 18.78C7.45 18.92 7.24 19 7 19C6.45 19 6 18.55 6 18V3C6 2.45 6.45 2 7 2C7.24 2 7.47 2.09 7.64 2.23L19.14 11.74C19.64 12.13 19.55 12.9 18.97 13.17L14.93 14.84L17.11 19.58C17.35 20.08 17.14 20.68 16.64 20.92L13.64 21.97Z"/>
            </svg>
            <span>Großer Mauszeiger</span>
          </button>

          <button
            @click="store.toggleSetting('focusHighlight')"
            class="a11y-toggle"
            :class="{ 'a11y-toggle--active': store.focusHighlight }"
          >
            <svg class="a11y-toggle__icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 3h6v2H5v4H3V3zm0 12h2v4h4v2H3v-6zm18 6h-6v-2h4v-4h2v6zm0-12h-2V5h-4V3h6v6z"/>
            </svg>
            <span>Fokus-Hervorhebung</span>
          </button>

          <button
            @click="store.toggleSetting('readableFont')"
            class="a11y-toggle"
            :class="{ 'a11y-toggle--active': store.readableFont }"
          >
            <svg class="a11y-toggle__icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9.93 13.5h4.14L12 7.98zM20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-4.05 16.5l-1.14-3H9.17l-1.12 3H5.96l5.11-13h1.86l5.11 13h-2.09z"/>
            </svg>
            <span>Lesbare Schrift</span>
          </button>
        </div>

        <!-- Saturation -->
        <div class="a11y-section">
          <div class="a11y-section__label">Farbsättigung</div>
          <div class="a11y-size-control">
            <button
              @click="store.decreaseSaturation()"
              :disabled="store.saturation <= 0"
              class="a11y-size-btn"
              aria-label="Sättigung verringern"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8z"/>
              </svg>
              <span class="a11y-size-btn__icon">−</span>
            </button>
            <span class="a11y-size-value">{{ store.saturation }}%</span>
            <button
              @click="store.increaseSaturation()"
              :disabled="store.saturation >= 150"
              class="a11y-size-btn"
              aria-label="Sättigung erhöhen"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8z"/>
              </svg>
              <span class="a11y-size-btn__icon">+</span>
            </button>
          </div>
        </div>

        <!-- Reset Button -->
        <button
          @click="store.reset()"
          class="a11y-reset"
          :disabled="!store.hasActiveSettings"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
          </svg>
          Zurücksetzen
        </button>
      </div>
    </div>
  </Transition>

  <!-- Reading Ruler -->
  <div
    v-if="store.readingRuler && store.isOpen === false"
    class="a11y-ruler"
    :style="{ top: store.rulerY + 'px' }"
  />
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue';
import { useAccessibilityStore } from '@/stores/accessibilityStore';

const store = useAccessibilityStore();

const handleMouseMove = (e) => {
  if (store.readingRuler) {
    store.updateRulerPosition(e.clientY);
  }
};

const handleKeydown = (e) => {
  if (e.key === 'Escape' && store.isOpen) {
    store.close();
  }
};

onMounted(() => {
  store.init();
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style lang="scss">
// Floating Action Button
.a11y-fab {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 9999;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #001c41;
  color: white;
  border: 3px solid white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: #ff9600;
    border-color: white;
    transform: scale(1.1);
  }

  &--active {
    background: #ff9600;

    &::after {
      content: '';
      position: absolute;
      top: -4px;
      right: -4px;
      width: 14px;
      height: 14px;
      background: #22c55e;
      border-radius: 50%;
      border: 2px solid white;
    }
  }

  @media (max-width: 767px) {
    bottom: 80px;
    width: 48px;
    height: 48px;
  }
}

// Panel
.a11y-panel {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  padding: 20px;

  @media (min-width: 768px) {
    align-items: center;
    padding: 20px 20px 20px 90px;
  }
}

.a11y-panel__content {
  background: white;
  border-radius: 16px;
  padding: 20px;
  max-width: 360px;
  width: 100%;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.a11y-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e5e7eb;
}

.a11y-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.25rem;
  font-weight: bold;
  color: #001c41;
  margin: 0;
}

.a11y-panel__close {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #6b7280;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #001c41;
  }
}

// Section
.a11y-section {
  margin-bottom: 20px;
}

.a11y-section__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
}

// Size Control (Font size, Saturation)
.a11y-size-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: #f3f4f6;
  border-radius: 12px;
  padding: 10px;
}

.a11y-size-btn {
  display: flex;
  align-items: center;
  gap: 2px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  color: #001c41;
  font-weight: 600;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #ff9600;
    color: white;
    border-color: #ff9600;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.a11y-size-btn__icon {
  font-size: 1rem;
  font-weight: bold;
}

.a11y-size-value {
  font-size: 1rem;
  font-weight: bold;
  color: #001c41;
  min-width: 50px;
  text-align: center;
}

// Toggle Buttons
.a11y-toggles {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
}

.a11y-toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #f3f4f6;
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 12px 8px;
  cursor: pointer;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  min-height: 80px;
  box-sizing: border-box;

  &:hover {
    background: #e5e7eb;
  }

  &--active {
    background: #001c41;
    color: white;
    border-color: #ff9600;

    .a11y-toggle__icon {
      color: #ff9600;
    }
  }
}

.a11y-toggle__icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: #6b7280;
}

// Reset Button
.a11y-reset {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: #f3f4f6;
  border: none;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e5e7eb;
    color: #001c41;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// Reading Ruler
.a11y-ruler {
  position: fixed;
  left: 0;
  right: 0;
  height: 40px;
  background: rgba(255, 150, 0, 0.15);
  border-top: 2px solid #ff9600;
  border-bottom: 2px solid #ff9600;
  pointer-events: none;
  z-index: 9998;
  transform: translateY(-50%);
}

// Slide Transition
.a11y-slide-enter-active,
.a11y-slide-leave-active {
  transition: opacity 0.3s ease;

  .a11y-panel__content {
    transition: transform 0.3s ease;
  }
}

.a11y-slide-enter-from,
.a11y-slide-leave-to {
  opacity: 0;

  .a11y-panel__content {
    transform: translateX(-20px);
  }
}

// ================================================
// GLOBAL ACCESSIBILITY STYLES
// ================================================

// Exclude the accessibility panel from global styles
.a11y-panel,
.a11y-panel * {
  line-height: normal !important;
  letter-spacing: normal !important;
  word-spacing: normal !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  filter: none !important;
  border-color: inherit !important;
}

// High Contrast
body.a11y-high-contrast {
  *:not(.a11y-panel):not(.a11y-panel *):not(.a11y-fab) {
    border-color: #000 !important;
  }

  a:not(.a11y-panel a):not(.a11y-fab),
  button:not(.a11y-panel button):not(.a11y-fab) {
    outline: 2px solid currentColor !important;
    outline-offset: 2px;
  }
}

// Line Spacing
body.a11y-line-spacing {
  *:not(.a11y-panel):not(.a11y-panel *):not(.a11y-fab) {
    line-height: 2 !important;
  }
}

// Letter Spacing
body.a11y-letter-spacing {
  *:not(.a11y-panel):not(.a11y-panel *):not(.a11y-fab) {
    letter-spacing: 0.12em !important;
    word-spacing: 0.2em !important;
  }
}

// Highlight Links
body.a11y-highlight-links {
  a:not(.a11y-panel a):not(.a11y-fab) {
    text-decoration: underline !important;
    text-decoration-thickness: 2px !important;
    text-underline-offset: 3px !important;
    background-color: rgba(255, 150, 0, 0.2) !important;
    padding: 2px 4px !important;
    border-radius: 2px !important;
  }
}

// Stop Animations
body.a11y-stop-animations {
  *:not(.a11y-panel):not(.a11y-panel *):not(.a11y-fab),
  *:not(.a11y-panel):not(.a11y-panel *)::before,
  *:not(.a11y-panel):not(.a11y-panel *)::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
    scroll-behavior: auto !important;
  }
}

// Large Cursor
body.a11y-large-cursor {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24'%3E%3Cpath fill='%23000' stroke='%23fff' stroke-width='1' d='M7 2l12 11.5-5.5 1 3 5.5-2.5 1.5-3-5.5-4 3.5z'/%3E%3C/svg%3E") 0 0, auto !important;

  a, button, [role="button"], input, select, textarea, label {
    cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24'%3E%3Cpath fill='%23ff9600' stroke='%23000' stroke-width='1' d='M7 2l12 11.5-5.5 1 3 5.5-2.5 1.5-3-5.5-4 3.5z'/%3E%3C/svg%3E") 0 0, pointer !important;
  }
}

// Focus Highlight
body.a11y-focus-highlight {
  *:focus:not(.a11y-panel *:focus):not(.a11y-fab:focus) {
    outline: 4px solid #ff9600 !important;
    outline-offset: 4px !important;
    box-shadow: 0 0 0 6px rgba(255, 150, 0, 0.3) !important;
  }

  *:focus:not(:focus-visible):not(.a11y-panel *):not(.a11y-fab) {
    outline: 4px solid #ff9600 !important;
  }
}

// Readable Font
body.a11y-readable-font {
  *:not(.a11y-panel):not(.a11y-panel *):not(.a11y-fab) {
    font-family: Arial, Helvetica, sans-serif !important;
  }
}

// Apply filters via CSS variable (set by store)
body {
  #app {
    filter: var(--a11y-filter, none);
  }
}

// Ensure panel is not affected by filters
.a11y-panel,
.a11y-fab {
  filter: none !important;
}
</style>
